import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Building2, Plus, Edit, Power, PowerOff, RefreshCw, Search, Users,
  Activity, Shield, AlertTriangle, AlertCircle, Info, CheckCircle,
  Wrench, ChevronRight, Eye, Clock, Headphones, LogOut, Lock,
  BarChart3, FileText, ShieldCheck, Ban, RotateCcw, KeyRound,
  Settings, Layers, UserX, Database, Calendar, ArrowRight,
  BrainCircuit, Sparkles, Loader2, ArrowUpRight, Play, Copy, User, Mail, KeyRound as KeyRound2,
} from 'lucide-react';

const API = API_URL;
const getToken = () => localStorage.getItem('punchtm-token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// ─── Supervision Dashboard Tab ─────────────────────────────────

function SupervisionTab({ overview, loading, onRefresh, onEnterSupport, onOpenTenant }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const kpis = overview?.kpis || {};
  const tenants = overview?.tenants || [];

  return (
    <div className="space-y-6" data-testid="supervision-tab">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard label="Tenants" value={kpis.totalTenants} icon={Building2} color="slate" />
        <KpiCard label="Actifs" value={kpis.activeTenants} icon={Power} color="emerald" />
        <KpiCard label="Suspendus" value={kpis.suspendedTenants} icon={Ban} color="red" />
        <KpiCard label="Utilisateurs" value={kpis.totalUsers} icon={Users} color="blue" />
        <KpiCard label="Employés" value={kpis.totalEmployees} icon={Activity} color="violet" />
        <KpiCard
          label="Problèmes"
          value={kpis.criticalIssues + kpis.warnings}
          icon={kpis.criticalIssues > 0 ? AlertCircle : CheckCircle}
          color={kpis.criticalIssues > 0 ? "red" : "emerald"}
        />
      </div>

      {/* Tenant Health Overview */}
      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardHeader className="border-b dark:border-slate-800 flex flex-row items-center justify-between py-4">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 size={18} />
              État des tenants
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">Vue rapide de la santé de chaque client</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} className="gap-1.5">
            <RefreshCw size={14} />
            Actualiser
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 dark:bg-slate-800/50">
                <TableHead className="font-semibold text-xs">Entreprise</TableHead>
                <TableHead className="font-semibold text-xs">Statut</TableHead>
                <TableHead className="font-semibold text-xs text-center">Employés</TableHead>
                <TableHead className="font-semibold text-xs text-center">Utilisateurs</TableHead>
                <TableHead className="font-semibold text-xs text-center">Santé</TableHead>
                <TableHead className="font-semibold text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8 text-sm">
                    Aucun tenant enregistré
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((t) => (
                  <TableRow key={t.id} className="group" data-testid={`tenant-health-${t.code}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm dark:text-slate-200">{t.name}</span>
                        <code className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-500">{t.code}</code>
                        {t.isDemo && (
                          <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 text-[10px] px-1.5 py-0">
                            DÉMO
                          </Badge>
                        )}
                        {t.protected && (
                          <Lock size={12} className="text-amber-500" title="Protégé" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {t.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px]">Actif</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px]">Suspendu</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-medium text-sm">{t.employeeCount}</TableCell>
                    <TableCell className="text-center font-medium text-sm">{t.userCount}</TableCell>
                    <TableCell className="text-center">
                      <HealthBadge health={t.health} issueCount={t.issueCount} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end opacity-60 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm" variant="outline"
                          className="h-7 px-2 gap-1 text-xs"
                          onClick={() => onOpenTenant(t)}
                          title="Voir les détails"
                          data-testid={`view-tenant-${t.code}`}
                        >
                          <Eye size={12} />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="h-7 px-2 gap-1 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950"
                          onClick={() => onEnterSupport(t)}
                          title="Intervenir en mode support"
                          data-testid={`support-tenant-${t.code}`}
                        >
                          <Headphones size={12} />
                          Intervenir
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  const colors = {
    slate: 'from-slate-50 to-slate-100/80 dark:from-slate-900 dark:to-slate-800/50',
    emerald: 'from-emerald-50 to-emerald-100/80 dark:from-emerald-950/30 dark:to-emerald-900/20',
    red: 'from-red-50 to-red-100/80 dark:from-red-950/30 dark:to-red-900/20',
    blue: 'from-blue-50 to-blue-100/80 dark:from-blue-950/30 dark:to-blue-900/20',
    violet: 'from-violet-50 to-violet-100/80 dark:from-violet-950/30 dark:to-violet-900/20',
  };
  const iconColors = {
    slate: 'text-slate-400', emerald: 'text-emerald-500', red: 'text-red-500',
    blue: 'text-blue-500', violet: 'text-violet-500',
  };
  const textColors = {
    slate: 'text-slate-500 dark:text-slate-400', emerald: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-red-600 dark:text-red-400', blue: 'text-blue-600 dark:text-blue-400',
    violet: 'text-violet-600 dark:text-violet-400',
  };

  return (
    <Card className={`bg-gradient-to-br ${colors[color]} border dark:border-slate-800`}>
      <CardContent className="p-3 flex items-center justify-between">
        <div>
          <p className={`text-[11px] font-medium ${textColors[color]}`}>{label}</p>
          <p className="text-2xl font-bold dark:text-slate-100">{value ?? 0}</p>
        </div>
        <Icon className={iconColors[color]} size={24} />
      </CardContent>
    </Card>
  );
}

function HealthBadge({ health, issueCount }) {
  if (health === 'healthy') {
    return <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] gap-1"><CheckCircle size={10} />OK</Badge>;
  }
  if (health === 'critical') {
    return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] gap-1"><AlertCircle size={10} />{issueCount}</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] gap-1"><AlertTriangle size={10} />{issueCount}</Badge>;
}


// ─── Tenants Management Tab ────────────────────────────────────

function TenantsTab({ overview, onRefresh, onEditTenant, onCreateTenant, onSuspend, onReactivate, onEnterSupport, onResetAdmin, onViewConfig }) {
  const [search, setSearch] = useState('');
  const tenants = (overview?.tenants || []).filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.code?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="tenants-tab">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 text-sm" />
        </div>
        <Button onClick={onCreateTenant} className="gap-1.5 h-9 text-sm" data-testid="add-tenant-btn">
          <Plus size={16} />
          Nouveau tenant
        </Button>
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80 dark:bg-slate-800/50">
                <TableHead className="font-semibold text-xs">Entreprise</TableHead>
                <TableHead className="font-semibold text-xs">Contact</TableHead>
                <TableHead className="font-semibold text-xs text-center">Employés</TableHead>
                <TableHead className="font-semibold text-xs text-center">Statut</TableHead>
                <TableHead className="font-semibold text-xs text-center">Config</TableHead>
                <TableHead className="font-semibold text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} className="group" data-testid={`tenant-row-${t.code}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm dark:text-slate-200">{t.name}</span>
                      <code className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-500">{t.code}</code>
                      {t.isDemo && <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">DÉMO</Badge>}
                      {t.protected && <Lock size={12} className="text-amber-500" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 dark:text-slate-400">
                    {t.employeeCount} emp. / {t.userCount} util.
                  </TableCell>
                  <TableCell className="text-center font-medium text-sm">{t.employeeCount}</TableCell>
                  <TableCell className="text-center">
                    {t.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px]"><Power size={10} className="mr-0.5" />Actif</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px]"><PowerOff size={10} className="mr-0.5" />Suspendu</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <HealthBadge health={t.health} issueCount={t.issueCount} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onEditTenant(t)} title="Modifier" data-testid={`edit-tenant-${t.code}`}>
                        <Edit size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onViewConfig(t.id)} title="Config" data-testid={`config-tenant-${t.code}`}>
                        <Settings size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400" onClick={() => onEnterSupport(t)} title="Support">
                        <Headphones size={12} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onResetAdmin(t)} title="Réinitialiser admin" data-testid={`reset-admin-${t.code}`}>
                        <KeyRound size={12} />
                      </Button>
                      {t.active ? (
                        <Button size="sm" variant="outline" className="h-7 px-2 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400" onClick={() => onSuspend(t)} disabled={t.protected} title={t.protected ? "Protégé" : "Suspendre"}>
                          <Ban size={12} />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 px-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50" onClick={() => onReactivate(t)} title="Réactiver">
                          <RotateCcw size={12} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Diagnostic Tab ────────────────────────────────────────────

function DiagnosticTab({ diagnostic, loading, onRefresh }) {
  const categoryLabels = {
    configuration: { label: 'Configuration', icon: Settings, color: 'text-blue-600' },
    permissions: { label: 'Permissions', icon: Shield, color: 'text-amber-600' },
    donnees_manquantes: { label: 'Données manquantes', icon: Database, color: 'text-slate-600' },
    paie: { label: 'Paie', icon: Calendar, color: 'text-green-600' },
    planification: { label: 'Planification', icon: Layers, color: 'text-violet-600' },
    autre: { label: 'Autre', icon: Info, color: 'text-slate-500' },
  };

  const categories = diagnostic?.categories || {};
  const summary = diagnostic?.issuesSummary || {};

  return (
    <div className="space-y-4" data-testid="diagnostic-tab">
      {/* Summary Badges */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge className={`${summary.critical > 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-slate-100 text-slate-500'} gap-1`}>
          <AlertCircle size={12} />
          {summary.critical || 0} Critiques
        </Badge>
        <Badge className={`${summary.warning > 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-slate-100 text-slate-500'} gap-1`}>
          <AlertTriangle size={12} />
          {summary.warning || 0} Avertissements
        </Badge>
        <Badge className={`${summary.info > 0 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-slate-100 text-slate-500'} gap-1`}>
          <Info size={12} />
          {summary.info || 0} Informations
        </Badge>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="ml-auto gap-1.5 h-7 text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="animate-spin text-primary" size={32} />
        </div>
      ) : Object.keys(categories).length === 0 ? (
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="text-center py-16">
            <CheckCircle className="mx-auto text-emerald-500 mb-3" size={40} />
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Aucun problème détecté</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">La plateforme fonctionne correctement</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(categories).map(([catKey, issues]) => {
          const cat = categoryLabels[catKey] || categoryLabels.autre;
          const CatIcon = cat.icon;
          return (
            <Card key={catKey} className="dark:bg-slate-900 dark:border-slate-800">
              <CardHeader className="border-b dark:border-slate-800 py-3">
                <CardTitle className={`text-sm font-semibold flex items-center gap-2 ${cat.color}`}>
                  <CatIcon size={16} />
                  {cat.label}
                  <Badge variant="outline" className="ml-1 text-[10px]">{issues.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y dark:divide-slate-800">
                {issues.map((issue, idx) => (
                  <div key={`${issue.entityId}-${idx}`} className={`px-4 py-3 flex items-center justify-between text-sm ${issue.severity === 'critical' ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`}>
                    <div className="flex items-start gap-2.5">
                      <SeverityIcon severity={issue.severity} />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900 dark:text-slate-200">{issue.entityName}</span>
                          <SeverityBadge severity={issue.severity} />
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{issue.message}</p>
                        <p className="text-[11px] text-primary dark:text-blue-400 mt-0.5 flex items-center gap-0.5">
                          <ChevronRight size={10} />{issue.suggestion}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}


// ─── Journal des interventions Tab ─────────────────────────────

function JournalTab({ interventions, loading, onRefresh }) {
  return (
    <div className="space-y-4" data-testid="journal-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Historique de toutes les interventions platform admin
        </p>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="gap-1.5 h-7 text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </Button>
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><RefreshCw className="animate-spin text-primary" size={32} /></div>
          ) : interventions.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="mx-auto text-slate-400 mb-3" size={40} />
              <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300">Aucune intervention enregistrée</h3>
              <p className="text-xs text-slate-400 mt-1">Les futures interventions support apparaîtront ici</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 dark:bg-slate-800/50">
                  <TableHead className="font-semibold text-xs">Date</TableHead>
                  <TableHead className="font-semibold text-xs">Admin</TableHead>
                  <TableHead className="font-semibold text-xs">Tenant</TableHead>
                  <TableHead className="font-semibold text-xs">Raison</TableHead>
                  <TableHead className="font-semibold text-xs">Statut</TableHead>
                  <TableHead className="font-semibold text-xs">Durée</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interventions.map((i) => {
                  const enteredAt = new Date(i.enteredAt);
                  const exitedAt = i.exitedAt ? new Date(i.exitedAt) : null;
                  const duration = exitedAt ? Math.round((exitedAt - enteredAt) / 60000) : null;
                  return (
                    <TableRow key={i.id}>
                      <TableCell className="text-xs text-slate-600 dark:text-slate-400">
                        {enteredAt.toLocaleDateString('fr-CA')} {enteredAt.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell className="text-xs font-medium dark:text-slate-200">{i.adminName}</TableCell>
                      <TableCell className="text-xs dark:text-slate-300">{i.tenantName}</TableCell>
                      <TableCell className="text-xs text-slate-600 dark:text-slate-400 max-w-[200px] truncate">{i.reason}</TableCell>
                      <TableCell>
                        {i.status === 'active' ? (
                          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px]">En cours</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-[10px]">Terminée</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {duration !== null ? `${duration} min` : <span className="text-blue-500">En cours</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ─── AI Assistant Tab ──────────────────────────────────────────

function AIAssistantTab({ tenants }) {
  const [selectedTenant, setSelectedTenant] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [fixLoading, setFixLoading] = useState({});
  const [fixConfirm, setFixConfirm] = useState(null);

  const fetchHistory = async (tenantId) => {
    try {
      const res = await axios.get(`${API}/platform/ai/history/${tenantId}`, authHeader());
      setHistory(res.data);
    } catch (e) { setHistory([]); }
  };

  const handleAnalyze = async () => {
    if (!selectedTenant) { toast.error('Sélectionnez un tenant'); return; }
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const res = await axios.post(`${API}/platform/ai/analyze/${selectedTenant}`, {}, authHeader());
      setAnalysis(res.data);
      fetchHistory(selectedTenant);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (selectedTenant) { fetchHistory(selectedTenant); }
    else { setHistory([]); setAnalysis(null); }
  }, [selectedTenant]);

  const handleFix = async (issue) => {
    setFixConfirm(issue);
  };

  const confirmFix = async () => {
    if (!fixConfirm) return;
    const issue = fixConfirm;
    const entity = issue.affected_entities?.[0];
    if (!entity) { toast.error('Aucune entité à corriger'); setFixConfirm(null); return; }

    const loadingKey = `${issue.fix_type}-${entity.id}`;
    setFixLoading(prev => ({ ...prev, [loadingKey]: true }));
    setFixConfirm(null);

    try {
      // For assignment fixes, we need a value - use first available
      let fixValue = null;
      if (issue.fix_type === 'assign_site' || issue.fix_type === 'assign_shift' || issue.fix_type === 'assign_zone') {
        const endpoint = issue.fix_type === 'assign_site' ? 'infrastructure/sites'
          : issue.fix_type === 'assign_shift' ? 'shifts'
          : 'infrastructure/zones';
        const listRes = await axios.get(`${API}/${endpoint}?tenantId=${selectedTenant}`, authHeader());
        const items = listRes.data;
        if (items.length === 0) {
          toast.error(`Aucun ${issue.fix_type.replace('assign_', '')} disponible`);
          return;
        }
        fixValue = items[0].id;
      }

      await axios.post(`${API}/platform/ai/fix/${selectedTenant}`, {
        issueType: issue.title,
        entityId: entity.id,
        entityType: issue.category,
        fixAction: issue.fix_type,
        fixValue: fixValue,
      }, authHeader());
      toast.success(`Correction appliquée: ${issue.title}`);
      // Re-analyze to refresh
      handleAnalyze();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de correction');
    } finally {
      setFixLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const a = analysis?.analysis;
  const issues = a?.issues || [];
  const critiques = issues.filter(i => i.level === 'critique');
  const avertissements = issues.filter(i => i.level === 'avertissement');
  const informations = issues.filter(i => i.level === 'information');

  const selectedTenantData = tenants?.find(t => t.id === selectedTenant);

  return (
    <div className="space-y-5" data-testid="ai-assistant-tab">
      {/* Header */}
      <Card className="dark:bg-slate-900 dark:border-slate-800 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <BrainCircuit size={20} className="text-indigo-600 dark:text-indigo-400" />
                Assistant IA de supervision
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md">
                Analyse intelligente des tenants: détection de problèmes, explication des causes et suggestions de correction guidées.
              </p>
            </div>
            <div className="flex items-end gap-3 w-full sm:w-auto">
              <div className="flex-1 sm:flex-initial sm:w-64 space-y-1">
                <Label className="text-[11px] text-slate-500">Tenant à analyser</Label>
                <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                  <SelectTrigger className="h-9 text-sm dark:bg-slate-800 dark:border-slate-700" data-testid="ai-tenant-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(tenants || []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="flex items-center gap-2">
                          {t.name}
                          {t.isDemo && <span className="text-[10px] text-purple-500">(démo)</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleAnalyze}
                disabled={!selectedTenant || analyzing}
                className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="ai-analyze-btn"
              >
                {analyzing ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {analyzing ? 'Analyse en cours...' : 'Analyser'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Analyzing state */}
      {analyzing && (
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardContent className="py-16 flex flex-col items-center">
            <div className="relative">
              <BrainCircuit size={40} className="text-indigo-500 animate-pulse" />
              <Sparkles size={16} className="text-purple-500 absolute -top-1 -right-1 animate-bounce" />
            </div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mt-4">
              Analyse en cours de <strong>{selectedTenantData?.name}</strong>...
            </p>
            <p className="text-xs text-slate-400 mt-1">GPT-5.2 analyse les données du tenant</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!analyzing && a && (
        <>
          {/* Summary Card */}
          <Card className="dark:bg-slate-900 dark:border-slate-800">
            <CardContent className="py-4">
              <div className="flex items-start gap-4">
                <div className="shrink-0 flex flex-col items-center gap-1">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold ${
                    a.health_score >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : a.health_score >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  }`}>
                    {a.health_score}
                  </div>
                  {analysis.delta && (
                    <DeltaBadge value={analysis.delta.healthScore} suffix="pts" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{analysis.tenantName}</h4>
                    <span className="text-[10px] text-slate-400">{new Date(analysis.analyzedAt).toLocaleString('fr-CA')}</span>
                    {analysis.delta && (
                      <span className="text-[10px] text-slate-400">vs {new Date(analysis.delta.previousDate).toLocaleDateString('fr-CA')}</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{a.summary}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {critiques.length > 0 && (
                      <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px] gap-1">
                        <AlertCircle size={10} />{critiques.length} Critique{critiques.length > 1 ? 's' : ''}
                        {analysis.delta && analysis.delta.criticalCount !== 0 && <DeltaInline value={analysis.delta.criticalCount} invert />}
                      </Badge>
                    )}
                    {avertissements.length > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px] gap-1">
                        <AlertTriangle size={10} />{avertissements.length} Avertissement{avertissements.length > 1 ? 's' : ''}
                        {analysis.delta && analysis.delta.warningCount !== 0 && <DeltaInline value={analysis.delta.warningCount} invert />}
                      </Badge>
                    )}
                    {informations.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px] gap-1">
                        <Info size={10} />{informations.length} Info{informations.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                    {issues.length === 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] gap-1">
                        <CheckCircle size={10} />Aucun problème
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Issue Groups */}
          {[
            { label: 'Problèmes critiques', items: critiques, severity: 'critique', color: 'red' },
            { label: 'Avertissements', items: avertissements, severity: 'avertissement', color: 'amber' },
            { label: 'Informations', items: informations, severity: 'information', color: 'blue' },
          ].filter(g => g.items.length > 0).map((group) => (
            <Card key={group.severity} className="dark:bg-slate-900 dark:border-slate-800">
              <CardHeader className="border-b dark:border-slate-800 py-3">
                <CardTitle className={`text-sm font-semibold flex items-center gap-2 text-${group.color}-600 dark:text-${group.color}-400`}>
                  {group.severity === 'critique' ? <AlertCircle size={16} /> : group.severity === 'avertissement' ? <AlertTriangle size={16} /> : <Info size={16} />}
                  {group.label}
                  <Badge variant="outline" className="ml-1 text-[10px]">{group.items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 divide-y dark:divide-slate-800">
                {group.items.map((issue, idx) => {
                  const entity = issue.affected_entities?.[0];
                  const loadingKey = entity ? `${issue.fix_type}-${entity.id}` : '';
                  return (
                    <div key={idx} className={`px-4 py-3.5 ${group.severity === 'critique' ? 'bg-red-50/50 dark:bg-red-950/10' : ''}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{issue.title}</h5>
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{issue.description}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2.5">
                            <div className="text-[11px]">
                              <span className="text-slate-400 font-medium">Cause:</span>
                              <span className="text-slate-600 dark:text-slate-300 ml-1">{issue.cause}</span>
                            </div>
                            <div className="text-[11px]">
                              <span className="text-slate-400 font-medium">Impact:</span>
                              <span className="text-slate-600 dark:text-slate-300 ml-1">{issue.impact}</span>
                            </div>
                            <div className="text-[11px]">
                              <span className="text-indigo-500 font-medium">Action:</span>
                              <span className="text-slate-600 dark:text-slate-300 ml-1">{issue.action}</span>
                            </div>
                          </div>
                          {issue.affected_entities?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {issue.affected_entities.slice(0, 5).map((e, eIdx) => (
                                <span key={eIdx} className="inline-flex items-center text-[10px] bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 text-slate-600 dark:text-slate-400">
                                  {e.name || e.code || e.id?.slice(0, 8)}
                                </span>
                              ))}
                              {issue.affected_entities.length > 5 && (
                                <span className="text-[10px] text-slate-400">+{issue.affected_entities.length - 5} autres</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          {issue.fixable && issue.fix_type && (
                            <Button
                              size="sm" variant="outline"
                              className="h-7 px-2.5 gap-1 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-950"
                              onClick={() => handleFix(issue)}
                              disabled={fixLoading[loadingKey]}
                              data-testid={`ai-fix-${idx}`}
                            >
                              {fixLoading[loadingKey] ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
                              Corriger
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}

          {/* Recommendations */}
          {a.recommendations?.length > 0 && (
            <Card className="dark:bg-slate-900 dark:border-slate-800">
              <CardHeader className="border-b dark:border-slate-800 py-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                  <Sparkles size={16} />
                  Recommandations
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3 space-y-2">
                {a.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <ArrowRight size={14} className="text-indigo-400 shrink-0 mt-0.5" />
                    <span className="text-slate-600 dark:text-slate-300">{rec}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 1 && (
            <Card className="dark:bg-slate-900 dark:border-slate-800">
              <CardHeader className="border-b dark:border-slate-800 py-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Clock size={16} />
                  Historique des analyses
                  <Badge variant="outline" className="ml-1 text-[10px]">{history.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 dark:bg-slate-800/50">
                      <TableHead className="font-semibold text-xs">Date</TableHead>
                      <TableHead className="font-semibold text-xs">Analyste</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Score</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Critiques</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Avertissements</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Infos</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h, idx) => {
                      const prev = history[idx + 1];
                      const scoreDelta = prev ? h.healthScore - prev.healthScore : null;
                      return (
                        <TableRow key={h.id} className={idx === 0 ? 'bg-indigo-50/50 dark:bg-indigo-950/10' : ''} data-testid={`history-row-${idx}`}>
                          <TableCell className="text-xs text-slate-600 dark:text-slate-400">
                            {new Date(h.analyzedAt).toLocaleDateString('fr-CA')}{' '}
                            <span className="text-slate-400">{new Date(h.analyzedAt).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</span>
                            {idx === 0 && <Badge className="ml-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[9px] py-0">Dernière</Badge>}
                          </TableCell>
                          <TableCell className="text-xs dark:text-slate-300">{h.adminName}</TableCell>
                          <TableCell className="text-center">
                            <span className={`text-sm font-bold ${
                              h.healthScore >= 80 ? 'text-emerald-600' : h.healthScore >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {h.healthScore}
                            </span>
                            {scoreDelta !== null && <DeltaInline value={scoreDelta} />}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {h.criticalCount > 0 ? <span className="text-red-600 font-medium">{h.criticalCount}</span> : <span className="text-slate-300">0</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {h.warningCount > 0 ? <span className="text-amber-600 font-medium">{h.warningCount}</span> : <span className="text-slate-300">0</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {h.infoCount > 0 ? <span className="text-blue-600">{h.infoCount}</span> : <span className="text-slate-300">0</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs font-medium dark:text-slate-300">{h.totalIssues}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Empty state */}
      {!analyzing && !a && (
        <>
          <Card className="dark:bg-slate-900 dark:border-slate-800">
            <CardContent className="text-center py-16">
              <BrainCircuit className="mx-auto text-slate-300 dark:text-slate-700 mb-3" size={48} />
              <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                {selectedTenant ? 'Lancez l\'analyse pour ce tenant' : 'Sélectionnez un tenant et lancez l\'analyse'}
              </h3>
              <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                L'assistant IA analysera la configuration, les données et la santé du tenant pour détecter les problèmes et proposer des corrections.
              </p>
              {history.length > 0 && (
                <p className="text-[11px] text-indigo-500 mt-2">{history.length} analyse(s) précédente(s) disponible(s)</p>
              )}
            </CardContent>
          </Card>

          {/* Show history even without current analysis */}
          {history.length > 0 && (
            <Card className="dark:bg-slate-900 dark:border-slate-800">
              <CardHeader className="border-b dark:border-slate-800 py-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Clock size={16} />
                  Historique des analyses
                  <Badge variant="outline" className="ml-1 text-[10px]">{history.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 dark:bg-slate-800/50">
                      <TableHead className="font-semibold text-xs">Date</TableHead>
                      <TableHead className="font-semibold text-xs">Analyste</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Score</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Critiques</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Avertissements</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Infos</TableHead>
                      <TableHead className="font-semibold text-xs text-center">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.slice(0, 10).map((h, idx) => {
                      const prev = history[idx + 1];
                      const scoreDelta = prev ? h.healthScore - prev.healthScore : null;
                      return (
                        <TableRow key={h.id} className={idx === 0 ? 'bg-indigo-50/50 dark:bg-indigo-950/10' : ''}>
                          <TableCell className="text-xs text-slate-600 dark:text-slate-400">
                            {new Date(h.analyzedAt).toLocaleDateString('fr-CA')}{' '}
                            <span className="text-slate-400">{new Date(h.analyzedAt).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</span>
                            {idx === 0 && <Badge className="ml-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[9px] py-0">Dernière</Badge>}
                          </TableCell>
                          <TableCell className="text-xs dark:text-slate-300">{h.adminName}</TableCell>
                          <TableCell className="text-center">
                            <span className={`text-sm font-bold ${
                              h.healthScore >= 80 ? 'text-emerald-600' : h.healthScore >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {h.healthScore}
                            </span>
                            {scoreDelta !== null && <DeltaInline value={scoreDelta} />}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {h.criticalCount > 0 ? <span className="text-red-600 font-medium">{h.criticalCount}</span> : <span className="text-slate-300">0</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {h.warningCount > 0 ? <span className="text-amber-600 font-medium">{h.warningCount}</span> : <span className="text-slate-300">0</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {h.infoCount > 0 ? <span className="text-blue-600">{h.infoCount}</span> : <span className="text-slate-300">0</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs font-medium dark:text-slate-300">{h.totalIssues}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Fix Confirmation Dialog */}
      <Dialog open={!!fixConfirm} onOpenChange={() => setFixConfirm(null)}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Wrench size={18} className="text-indigo-600" />
              Confirmer la correction
            </DialogTitle>
            <DialogDescription>
              {fixConfirm && (
                <span>
                  Action: <strong>{fixConfirm.action}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {fixConfirm && (
            <div className="space-y-3 py-2">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-3 space-y-1.5">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{fixConfirm.title}</p>
                <p className="text-xs text-slate-500">{fixConfirm.description}</p>
              </div>
              {fixConfirm.affected_entities?.length > 0 && (
                <div className="text-xs text-slate-500">
                  Éléments concernés: {fixConfirm.affected_entities.map(e => e.name || e.code).join(', ')}
                </div>
              )}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3">
                <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  Cette action sera journalisée et modifiera les données du tenant.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixConfirm(null)} className="h-9">Annuler</Button>
            <Button onClick={confirmFix} className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-ai-fix-btn">
              <Wrench size={14} />
              Appliquer la correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Delta Components ──────────────────────────────────────────

function DeltaBadge({ value, suffix = '' }) {
  if (value === null || value === undefined) return null;
  const isPositive = value > 0;
  const isZero = value === 0;
  if (isZero) return null;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
      isPositive ? 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/40'
      : 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/40'
    }`} data-testid="delta-badge">
      {isPositive ? '+' : ''}{value}{suffix}
    </span>
  );
}

function DeltaInline({ value, invert = false }) {
  if (value === null || value === undefined || value === 0) return null;
  const isGood = invert ? value < 0 : value > 0;
  return (
    <span className={`text-[9px] font-medium ml-0.5 ${isGood ? 'text-emerald-600' : 'text-red-600'}`}>
      ({value > 0 ? '+' : ''}{value})
    </span>
  );
}


// ─── Shared Components ─────────────────────────────────────────

function SeverityIcon({ severity }) {
  if (severity === 'critical') return <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={16} />;
  if (severity === 'warning') return <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />;
  return <Info className="text-blue-500 shrink-0 mt-0.5" size={16} />;
}

function SeverityBadge({ severity }) {
  if (severity === 'critical') return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-[10px]">Critique</Badge>;
  if (severity === 'warning') return <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-[10px]">Attention</Badge>;
  return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px]">Info</Badge>;
}


// ─── Main Page Component ───────────────────────────────────────

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_superadmin';

  // Data
  const [overview, setOverview] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diagLoading, setDiagLoading] = useState(false);
  const [journalLoading, setJournalLoading] = useState(false);

  // UI
  const [activeTab, setActiveTab] = useState('supervision');
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [supportTarget, setSupportTarget] = useState(null);
  const [supportReason, setSupportReason] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [configSheetOpen, setConfigSheetOpen] = useState(false);
  const [configData, setConfigData] = useState(null);
  const [tenantDetailOpen, setTenantDetailOpen] = useState(false);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', contactEmail: '', contactPhone: '', address: '', maxEmployees: 100, adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '' });
  const [createdCreds, setCreatedCreds] = useState(null);  // {email,password,firstName,lastName,tenantName,tenantCode}

  // ── Data fetching ──

  const fetchOverview = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      const res = await axios.get(`${API}/platform/overview`, authHeader());
      setOverview(res.data);
    } catch (e) { console.error(e); }
  }, [isPlatformAdmin]);

  const fetchDiagnostic = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      setDiagLoading(true);
      const res = await axios.get(`${API}/platform/diagnostic`, authHeader());
      setDiagnostic(res.data);
    } catch (e) { toast.error('Erreur diagnostic'); }
    finally { setDiagLoading(false); }
  }, [isPlatformAdmin]);

  const fetchJournal = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      setJournalLoading(true);
      const res = await axios.get(`${API}/platform/support/log`, authHeader());
      setInterventions(res.data);
    } catch (e) { console.error(e); }
    finally { setJournalLoading(false); }
  }, [isPlatformAdmin]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchDiagnostic(), fetchJournal()]);
      setLoading(false);
    };
    init();
  }, [fetchOverview, fetchDiagnostic, fetchJournal]);

  // ── Actions ──

  const handleCreateTenant = () => {
    setEditingTenant(null);
    setFormData({ name: '', code: '', contactEmail: '', contactPhone: '', address: '', maxEmployees: 100, adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '' });
    setTenantDialogOpen(true);
  };

  const handleEditTenant = (t) => {
    setEditingTenant(t);
    setFormData({ name: t.name, code: t.code, contactEmail: t.contactEmail || '', contactPhone: t.contactPhone || '', address: t.address || '', maxEmployees: t.maxEmployees || 100, adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '' });
    setTenantDialogOpen(true);
  };

  const handleSaveTenant = async () => {
    if (!formData.name || !formData.code || !formData.contactEmail) {
      toast.error('Champs obligatoires manquants');
      return;
    }
    // When creating: admin fields are required (firstName + lastName + email)
    if (!editingTenant && (!formData.adminFirstName || !formData.adminLastName || !formData.adminEmail)) {
      toast.error('Renseignez le prenom, nom et email de l\'administrateur du tenant');
      return;
    }
    try {
      if (editingTenant) {
        // Only send tenant fields when editing (admin fields not applicable)
        const { adminFirstName, adminLastName, adminEmail, adminPassword, ...tenantOnly } = formData;
        await axios.put(`${API}/tenants/${editingTenant.id}`, tenantOnly, authHeader());
        toast.success('Tenant modifie');
        setTenantDialogOpen(false);
      } else {
        const res = await axios.post(`${API}/tenants`, formData, authHeader());
        toast.success('Tenant cree');
        setTenantDialogOpen(false);
        if (res.data?.adminCredentials) {
          setCreatedCreds({
            ...res.data.adminCredentials,
            tenantName: res.data.name,
            tenantCode: res.data.code,
          });
        }
      }
      fetchOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleSuspend = async (t) => {
    if (t.protected) { toast.error('Ce tenant est protégé'); return; }
    try {
      await axios.post(`${API}/platform/tenant/${t.id}/suspend`, {}, authHeader());
      toast.success(`${t.name} suspendu`);
      fetchOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleReactivate = async (t) => {
    try {
      await axios.post(`${API}/platform/tenant/${t.id}/reactivate`, {}, authHeader());
      toast.success(`${t.name} réactivé`);
      fetchOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleOpenSupport = (t) => {
    setSupportTarget(t);
    setSupportReason('');
    setSupportDialogOpen(true);
  };

  const handleEnterSupport = async () => {
    if (!supportReason.trim()) { toast.error('Veuillez indiquer la raison'); return; }
    try {
      const res = await axios.post(`${API}/platform/support/enter/${supportTarget.id}`, { reason: supportReason }, authHeader());
      // Store original token and intervention ID
      localStorage.setItem('punchtm-original-token', getToken());
      localStorage.setItem('punchtm-support-intervention', res.data.interventionId);
      localStorage.setItem('punchtm-support-mode', 'true');
      localStorage.setItem('punchtm-support-tenant', supportTarget.name);
      localStorage.setItem('punchtm-token', res.data.access_token);
      toast.success(`Mode support activé pour ${supportTarget.name}`);
      setSupportDialogOpen(false);
      window.location.href = '/admin/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d\'activation du mode support');
    }
  };

  const handleResetAdmin = (t) => {
    setResetTarget(t);
    setResetPassword('');
    setResetDialogOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!resetPassword || resetPassword.length < 8) { toast.error('Mot de passe trop court (min 8 caractères)'); return; }
    try {
      const res = await axios.post(`${API}/platform/tenant/${resetTarget.id}/reset-admin`, { newPassword: resetPassword }, authHeader());
      toast.success(`Mot de passe réinitialisé pour ${res.data.email}`);
      setResetDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleViewConfig = async (tenantId) => {
    try {
      const res = await axios.get(`${API}/platform/tenant/${tenantId}/config-status`, authHeader());
      setConfigData(res.data);
      setConfigSheetOpen(true);
    } catch (e) { toast.error('Erreur'); }
  };

  const handleOpenTenant = (t) => {
    setTenantDetail(t);
    setTenantDetailOpen(true);
  };

  // ── Access Check ──

  if (!isPlatformAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center dark:bg-slate-900">
          <PowerOff size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">Accès refusé</h2>
          <p className="text-slate-500 dark:text-slate-400">Cette page est réservée aux administrateurs de la plateforme.</p>
        </Card>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="space-y-5" data-testid="platform-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ShieldCheck className="text-primary" size={22} />
            Administration Plateforme
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
            Supervision, diagnostic et intervention
          </p>
        </div>
        {overview && (
          <div className="flex gap-2 flex-wrap">
            {overview.kpis.criticalIssues > 0 && (
              <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 py-0.5 px-2.5 text-xs animate-pulse gap-1">
                <AlertCircle size={12} />
                {overview.kpis.criticalIssues} critique(s)
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 max-w-2xl h-9">
          <TabsTrigger value="supervision" className="gap-1.5 text-xs" data-testid="tab-supervision">
            <BarChart3 size={14} />
            Supervision
          </TabsTrigger>
          <TabsTrigger value="tenants" className="gap-1.5 text-xs" data-testid="tab-tenants">
            <Building2 size={14} />
            Tenants
          </TabsTrigger>
          <TabsTrigger value="diagnostic" className="gap-1.5 text-xs" data-testid="tab-diagnostic">
            <Wrench size={14} />
            Diagnostic
          </TabsTrigger>
          <TabsTrigger value="ai-assistant" className="gap-1.5 text-xs" data-testid="tab-ai-assistant">
            <BrainCircuit size={14} />
            Assistant IA
          </TabsTrigger>
          <TabsTrigger value="journal" className="gap-1.5 text-xs" data-testid="tab-journal">
            <FileText size={14} />
            Journal
          </TabsTrigger>
        </TabsList>

        <TabsContent value="supervision" className="mt-4">
          <SupervisionTab
            overview={overview} loading={loading}
            onRefresh={fetchOverview}
            onEnterSupport={handleOpenSupport}
            onOpenTenant={handleOpenTenant}
          />
        </TabsContent>

        <TabsContent value="tenants" className="mt-4">
          <TenantsTab
            overview={overview}
            onRefresh={fetchOverview}
            onEditTenant={handleEditTenant}
            onCreateTenant={handleCreateTenant}
            onSuspend={handleSuspend}
            onReactivate={handleReactivate}
            onEnterSupport={handleOpenSupport}
            onResetAdmin={handleResetAdmin}
            onViewConfig={handleViewConfig}
          />
        </TabsContent>

        <TabsContent value="diagnostic" className="mt-4">
          <DiagnosticTab diagnostic={diagnostic} loading={diagLoading} onRefresh={fetchDiagnostic} />
        </TabsContent>

        <TabsContent value="ai-assistant" className="mt-4">
          <AIAssistantTab tenants={overview?.tenants || []} />
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          <JournalTab interventions={interventions} loading={journalLoading} onRefresh={fetchJournal} />
        </TabsContent>
      </Tabs>

      {/* ── Dialogs ── */}

      {/* Create/Edit Tenant Dialog */}
      <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
        <DialogContent className="sm:max-w-lg dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Building2 size={18} />
              {editingTenant ? 'Modifier tenant' : 'Nouveau tenant'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nom entreprise *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Acme Corp" className="h-9" data-testid="tenant-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Code *</Label>
                <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="ACME" maxLength={10} disabled={!!editingTenant} className="h-9" data-testid="tenant-code" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email contact *</Label>
              <Input type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} className="h-9" data-testid="tenant-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Téléphone</Label>
                <Input value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} className="h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max employés</Label>
                <Input type="number" value={formData.maxEmployees} onChange={(e) => setFormData({ ...formData, maxEmployees: parseInt(e.target.value) || 100 })} className="h-9" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Adresse</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="h-9" />
            </div>

            {!editingTenant && (
              <>
                <div className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-700">
                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <User size={13} className="text-primary" /> Administrateur du tenant (superutilisateur)
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Compte de connexion cree en meme temps que le tenant.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Prenom admin *</Label>
                    <Input value={formData.adminFirstName} onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })} placeholder="Aminata" className="h-9" data-testid="tenant-admin-firstname" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Nom admin *</Label>
                    <Input value={formData.adminLastName} onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })} placeholder="Diop" className="h-9" data-testid="tenant-admin-lastname" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email admin *</Label>
                  <Input type="email" value={formData.adminEmail} onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })} placeholder="admin@entreprise.com" className="h-9" data-testid="tenant-admin-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mot de passe (laisser vide pour auto-generer)</Label>
                  <Input value={formData.adminPassword} onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })} placeholder="Auto genere si vide" className="h-9 font-mono" data-testid="tenant-admin-password" />
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Un mot de passe sur (12 caracteres, sans $) sera genere si laisse vide.</p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTenantDialogOpen(false)} className="h-9">Annuler</Button>
            <Button onClick={handleSaveTenant} className="h-9" data-testid="save-tenant-btn">{editingTenant ? 'Modifier' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Created Dialog - shown after new tenant + admin creation */}
      <Dialog open={!!createdCreds} onOpenChange={(open) => !open && setCreatedCreds(null)}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800" data-testid="creds-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CheckCircle size={18} className="text-emerald-500" />
              Tenant cree - Identifiants admin
            </DialogTitle>
            <DialogDescription>
              Notez ces identifiants maintenant. Le mot de passe ne sera plus jamais affiche en clair.
            </DialogDescription>
          </DialogHeader>
          {createdCreds && (
            <div className="space-y-3 py-2">
              <div className="rounded-md border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 p-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Entreprise</p>
                    <p className="font-semibold">{createdCreds.tenantName} <span className="text-xs text-slate-400">({createdCreds.tenantCode})</span></p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Administrateur</p>
                  <p className="font-medium">{createdCreds.firstName} {createdCreds.lastName}</p>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-emerald-200 dark:border-emerald-900 pt-2">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Email</p>
                    <p className="font-mono text-xs truncate">{createdCreds.email}</p>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(createdCreds.email); toast.success('Email copie'); }} data-testid="copy-admin-email">
                    <Copy size={12} /> Copier
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Mot de passe</p>
                    <p className="font-mono text-xs truncate">{createdCreds.password}</p>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => { navigator.clipboard.writeText(createdCreds.password); toast.success('Mot de passe copie'); }} data-testid="copy-admin-password">
                    <Copy size={12} /> Copier
                  </Button>
                </div>
              </div>
              <div className="rounded-md border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-2.5">
                <p className="text-xs text-amber-800 dark:text-amber-400 flex items-start gap-1.5">
                  <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                  Transmettez ces identifiants au client par un canal securise (email chiffre, WhatsApp verifie...). Demandez-lui de changer le mot de passe apres la 1ere connexion.
                </p>
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Connexion : <span className="font-mono">/admin/login</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="h-9" onClick={() => {
              if (createdCreds) {
                const txt = `Entreprise: ${createdCreds.tenantName} (${createdCreds.tenantCode})\nAdmin: ${createdCreds.firstName} ${createdCreds.lastName}\nEmail: ${createdCreds.email}\nMot de passe: ${createdCreds.password}\nConnexion: /admin/login`;
                navigator.clipboard.writeText(txt);
                toast.success('Tous les identifiants copies');
              }
            }} data-testid="copy-all-creds">
              <Copy size={14} className="mr-1" /> Tout copier
            </Button>
            <Button className="h-9" onClick={() => setCreatedCreds(null)} data-testid="close-creds-dialog">J&apos;ai note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Support Mode Entry Dialog */}
      <Dialog open={supportDialogOpen} onOpenChange={setSupportDialogOpen}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Headphones size={18} className="text-blue-600" />
              Intervention support
            </DialogTitle>
            <DialogDescription>
              {supportTarget && (
                <span>Ouvrir le tenant <strong>{supportTarget.name}</strong> en mode support. Cette action sera journalisée.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Raison de l'intervention *</Label>
              <Textarea
                value={supportReason}
                onChange={(e) => setSupportReason(e.target.value)}
                placeholder="Ex: Problème de configuration signalé par le client..."
                className="min-h-[80px] text-sm"
                data-testid="support-reason"
              />
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                Vous serez connecté en tant que superutilisateur du tenant. Une bannière indiquera le mode support. Toutes vos actions seront journalisées.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupportDialogOpen(false)} className="h-9">Annuler</Button>
            <Button onClick={handleEnterSupport} className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="confirm-support-btn">
              <Headphones size={14} />
              Ouvrir en mode support
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Admin Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <KeyRound size={18} />
              Réinitialiser accès admin
            </DialogTitle>
            <DialogDescription>
              {resetTarget && <span>Réinitialiser le mot de passe du superutilisateur de <strong>{resetTarget.name}</strong></span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Nouveau mot de passe</Label>
              <Input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Minimum 8 caractères" className="h-9" data-testid="reset-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} className="h-9">Annuler</Button>
            <Button onClick={handleConfirmReset} className="h-9" data-testid="confirm-reset-btn">Réinitialiser</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Status Sheet */}
      <Sheet open={configSheetOpen} onOpenChange={setConfigSheetOpen}>
        <SheetContent className="dark:bg-slate-900 dark:border-slate-800 w-[400px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <Settings size={18} />
              État de configuration
            </SheetTitle>
            <SheetDescription>{configData?.tenantName}</SheetDescription>
          </SheetHeader>
          {configData && (
            <div className="mt-4 space-y-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">{configData.completeness}%</p>
                <p className="text-xs text-slate-500">Complétude</p>
              </div>
              <div className="space-y-2">
                {configData.checks?.map((check, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2.5 border rounded-md dark:border-slate-800">
                    <span className="text-sm dark:text-slate-300">{check.item}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{check.count}</span>
                      {check.status === 'ok' ? (
                        <CheckCircle size={16} className="text-emerald-500" />
                      ) : check.status === 'warning' ? (
                        <AlertTriangle size={16} className="text-amber-500" />
                      ) : (
                        <AlertCircle size={16} className="text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Tenant Detail Sheet */}
      <Sheet open={tenantDetailOpen} onOpenChange={setTenantDetailOpen}>
        <SheetContent className="dark:bg-slate-900 dark:border-slate-800 w-[420px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-base">
              <Building2 size={18} />
              {tenantDetail?.name}
            </SheetTitle>
            <SheetDescription>
              <code className="text-xs">{tenantDetail?.code}</code>
              {tenantDetail?.isDemo && <Badge className="ml-2 bg-purple-100 text-purple-700 text-[10px]">DÉMO</Badge>}
            </SheetDescription>
          </SheetHeader>
          {tenantDetail && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Card className="p-3 text-center dark:bg-slate-800 dark:border-slate-700">
                  <p className="text-xl font-bold text-blue-600">{tenantDetail.employeeCount}</p>
                  <p className="text-[11px] text-slate-500">Employés</p>
                </Card>
                <Card className="p-3 text-center dark:bg-slate-800 dark:border-slate-700">
                  <p className="text-xl font-bold text-violet-600">{tenantDetail.userCount}</p>
                  <p className="text-[11px] text-slate-500">Utilisateurs</p>
                </Card>
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</h4>
                <div className="flex items-center gap-2">
                  <HealthBadge health={tenantDetail.health} issueCount={tenantDetail.issueCount} />
                  {tenantDetail.active ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Actif</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 text-[10px]">Suspendu</Badge>
                  )}
                </div>
              </div>

              {tenantDetail.issues?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Problèmes détectés</h4>
                  {tenantDetail.issues.map((issue, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 border rounded-md dark:border-slate-800 text-xs">
                      <SeverityIcon severity={issue.severity} />
                      <span className="dark:text-slate-300">{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2 pt-2">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</h4>
                <div className="flex flex-col gap-2">
                  <Button variant="outline" className="justify-start gap-2 h-9 text-sm" onClick={() => { setTenantDetailOpen(false); handleOpenSupport(tenantDetail); }}>
                    <Headphones size={14} className="text-blue-600" />
                    Intervenir en mode support
                  </Button>
                  <Button variant="outline" className="justify-start gap-2 h-9 text-sm" onClick={() => { setTenantDetailOpen(false); handleViewConfig(tenantDetail.id); }}>
                    <Settings size={14} />
                    Voir la configuration
                  </Button>
                  <Button variant="outline" className="justify-start gap-2 h-9 text-sm" onClick={() => { setTenantDetailOpen(false); handleResetAdmin(tenantDetail); }}>
                    <KeyRound size={14} />
                    Réinitialiser accès admin
                  </Button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
